import React from "react";
import SomethingWentWrongPage from "../../pages/SomethingWentWrongPage";
import { isChunkLoadError, reloadForFreshChunks } from "../../utils/chunkErrorRecovery";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level React error boundary. Catches uncaught render/lifecycle errors
 * and delegates to SomethingWentWrongPage for a consistent error UI.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
    // A lazy route's chunk can 404 after a new deploy replaces its hashed
    // filename — self-heal with a reload instead of leaving the fallback UI
    // up (resetting local state alone can't fix a stale module reference).
    if (isChunkLoadError(error)) reloadForFreshChunks();
  }

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <SomethingWentWrongPage
          error={this.state.error ?? undefined}
          onRetry={() => {
            if (chunkError) {
              // A user-initiated retry is a conscious action, not an
              // automatic loop risk — reload unconditionally here even if
              // the cooldown already blocked componentDidCatch's own attempt.
              window.location.reload();
              return;
            }
            this.setState({ hasError: false, error: null });
          }}
          onHome={() => { this.setState({ hasError: false, error: null }); window.location.href = "/"; }}
        />
      );
    }
    return this.props.children;
  }
}

